// src/utils/filterRegistry.js
// Registry-driven advanced kiosk filters (TKT-2026-102569).
//
// The backend (`GET /products/filters/registry`) is the source of truth; this
// module ships a static MIRROR of FILTER REGISTRY v1 used only as a fallback
// when the endpoint is unreachable (e.g. before PR #80 merges) and for default
// rendering. The per-game registry subset returned by `/:store/filters` is
// always preferred at runtime.
//
// Each entry: { key, gameIds:[categoryId], control, group, label, defaultEnabled }.
// control ∈ 'chips' | 'range' | 'typeahead' | 'toggle'.

import { formatMoney } from './money';

// categoryIds: MTG 1, YGO 2, Pokémon 3, FaB 62, One Piece 68, Lorcana 71.
const ALL_GAMES = [1, 2, 3, 62, 68, 71];
const FOIL_GAMES = [1, 3, 62, 68, 71]; // excludes YGO (2): no foil subtype

export const FILTER_REGISTRY_FALLBACK = [
    // ── UNIVERSAL ───────────────────────────────────────────────────────────
    { key: 'rarity', gameIds: ALL_GAMES, control: 'chips', group: 'Basics', label: 'Rarity', defaultEnabled: true },
    // Price is the in-stock price the card displays. Rendered by the dedicated
    // Min/Max control (see PRICE_FILTER_KEY), never the generic slider.
    // TKT-2026-105611
    { key: 'price', gameIds: ALL_GAMES, allGames: true, control: 'range', format: 'currency', group: 'Basics', label: 'Price', defaultEnabled: true },
    { key: 'foil', gameIds: FOIL_GAMES, control: 'toggle', group: 'Basics', label: 'Foil only', defaultEnabled: true },

    // ── MTG (1) ─────────────────────────────────────────────────────────────
    { key: 'color', gameIds: [1], control: 'chips', group: 'Card', label: 'Color', defaultEnabled: true },
    { key: 'colorIdentity', gameIds: [1], control: 'chips', group: 'Card', label: 'Color Identity', defaultEnabled: true },
    { key: 'cardType', gameIds: [1], control: 'chips', group: 'Card', label: 'Card Type', defaultEnabled: true },
    { key: 'cmc', gameIds: [1], control: 'range', group: 'Stats', label: 'Mana Value (CMC)', defaultEnabled: true },
    { key: 'legality', gameIds: [1], control: 'chips', group: 'Card', label: 'Format Legality', defaultEnabled: true },
    { key: 'creatureType', gameIds: [1], control: 'typeahead', group: 'Card', label: 'Creature Type', defaultEnabled: true },
    { key: 'keywords', gameIds: [1], control: 'chips', group: 'Card', label: 'Keywords', defaultEnabled: false },
    { key: 'artist', gameIds: [1], control: 'typeahead', group: 'Card', label: 'Artist', defaultEnabled: false },
    { key: 'powerToughness', gameIds: [1], control: 'range', group: 'Stats', label: 'Power / Toughness', defaultEnabled: false },

    // ── Pokémon (3) ─────────────────────────────────────────────────────────
    { key: 'pokemonType', gameIds: [3], control: 'chips', group: 'Card', label: 'Type', defaultEnabled: true },
    { key: 'hp', gameIds: [3], control: 'range', group: 'Stats', label: 'HP', defaultEnabled: true },
    { key: 'stage', gameIds: [3], control: 'chips', group: 'Card', label: 'Stage', defaultEnabled: true },
    { key: 'illustrator', gameIds: [3], control: 'typeahead', group: 'Card', label: 'Illustrator', defaultEnabled: false },
    { key: 'regulationMark', gameIds: [3], control: 'chips', group: 'Card', label: 'Regulation Mark', defaultEnabled: false },
    { key: 'weakness', gameIds: [3], control: 'chips', group: 'Card', label: 'Weakness', defaultEnabled: false },
    { key: 'retreatCost', gameIds: [3], control: 'range', group: 'Stats', label: 'Retreat Cost', defaultEnabled: false },

    // ── YGO (2) ─────────────────────────────────────────────────────────────
    { key: 'ygoCardType', gameIds: [2], control: 'chips', group: 'Card', label: 'Card Type', defaultEnabled: true },
    { key: 'attribute', gameIds: [2], control: 'chips', group: 'Card', label: 'Attribute', defaultEnabled: true },
    { key: 'monsterType', gameIds: [2], control: 'typeahead', group: 'Card', label: 'Monster Type', defaultEnabled: true },
    { key: 'atk', gameIds: [2], control: 'range', group: 'Stats', label: 'ATK', defaultEnabled: false },
    { key: 'def', gameIds: [2], control: 'range', group: 'Stats', label: 'DEF', defaultEnabled: false },

    // ── Lorcana (71) ────────────────────────────────────────────────────────
    { key: 'inkType', gameIds: [71], control: 'chips', group: 'Card', label: 'Ink Type', defaultEnabled: true },
    { key: 'lorcanaCardType', gameIds: [71], control: 'chips', group: 'Card', label: 'Card Type', defaultEnabled: true },
    { key: 'inkCost', gameIds: [71], control: 'range', group: 'Stats', label: 'Ink Cost', defaultEnabled: true },
    { key: 'lore', gameIds: [71], control: 'range', group: 'Stats', label: 'Lore', defaultEnabled: true },
    { key: 'strength', gameIds: [71], control: 'range', group: 'Stats', label: 'Strength', defaultEnabled: false },
    { key: 'willpower', gameIds: [71], control: 'range', group: 'Stats', label: 'Willpower', defaultEnabled: false },
    { key: 'inkable', gameIds: [71], control: 'toggle', group: 'Card', label: 'Inkable only', defaultEnabled: false },
    { key: 'property', gameIds: [71], control: 'typeahead', group: 'Card', label: 'Property', defaultEnabled: false },

    // ── One Piece (68) ──────────────────────────────────────────────────────
    { key: 'opColor', gameIds: [68], control: 'chips', group: 'Card', label: 'Color', defaultEnabled: true },
    { key: 'opCardType', gameIds: [68], control: 'chips', group: 'Card', label: 'Card Type', defaultEnabled: true },
    { key: 'opCost', gameIds: [68], control: 'range', group: 'Stats', label: 'Cost', defaultEnabled: true },
    { key: 'opPower', gameIds: [68], control: 'range', group: 'Stats', label: 'Power', defaultEnabled: true },
    { key: 'counter', gameIds: [68], control: 'range', group: 'Stats', label: 'Counter', defaultEnabled: false },
    { key: 'opAttribute', gameIds: [68], control: 'chips', group: 'Card', label: 'Attribute', defaultEnabled: false },
    { key: 'opSubtypes', gameIds: [68], control: 'typeahead', group: 'Card', label: 'Subtypes', defaultEnabled: false },

    // ── Flesh and Blood (62) ────────────────────────────────────────────────
    { key: 'fabCardType', gameIds: [62], control: 'chips', group: 'Card', label: 'Card Type', defaultEnabled: true },
    { key: 'fabClass', gameIds: [62], control: 'chips', group: 'Card', label: 'Class', defaultEnabled: true },
    { key: 'pitch', gameIds: [62], control: 'chips', group: 'Card', label: 'Pitch', defaultEnabled: true },
    { key: 'fabCost', gameIds: [62], control: 'range', group: 'Stats', label: 'Cost', defaultEnabled: true },
    { key: 'fabDefense', gameIds: [62], control: 'range', group: 'Stats', label: 'Defense', defaultEnabled: false },
];

// Order the registry `group`s consistently in the UI.
export const FILTER_GROUP_ORDER = ['Basics', 'Card', 'Stats', 'Search'];

// Registry keys that the kiosk renders through legacy, hand-built controls
// instead of the dynamic renderer (they already existed pre-102569). They are
// still emitted as registry keys in the query string.
//
// `price` is here for the same reason: it has its own Min/Max control (a
// slider across $0.15 to $1,410 cannot pick "$5 to $15"), and that control has
// to show with no game selected, which the per-game renderer never does.
export const LEGACY_RENDERED_KEYS = new Set(['rarity', 'color', 'colorIdentity', 'price']);

export const PRICE_FILTER_KEY = 'price';

// Map a registry chips/typeahead key → the legacy facet field the backend has
// always returned, so we can fall back to it when `facets[key]` is absent.
export const FACET_ALIAS = {
    rarity: 'rarities',
    color: 'colors',
    colorIdentity: 'colorIdentities',
    cardType: 'types',
    creatureType: 'creatureTypes',
    legality: 'legalStatuses',
};

// Toggle-control keys emit their own boolean param.
export const TOGGLE_KEYS = new Set(['foil', 'inkable']);

/**
 * Resolve the option list for a chips/typeahead registry key from a /filters
 * response, preferring the new `facets[key]` and falling back to the legacy
 * field. Returns a de-duplicated array.
 */
export function facetOptions(filterOptions, key) {
    const fromFacet = filterOptions?.facets?.[key];
    if (Array.isArray(fromFacet) && fromFacet.length) return [...new Set(fromFacet)];
    const aliasField = FACET_ALIAS[key];
    const legacy = aliasField ? filterOptions?.[aliasField] : undefined;
    if (Array.isArray(legacy)) return [...new Set(legacy)];
    return [];
}

/** Resolve numeric {min,max} bounds for a range key from a /filters response. */
export function rangeBounds(filterOptions, key) {
    const r = filterOptions?.ranges?.[key];
    if (r && Number.isFinite(Number(r.min)) && Number.isFinite(Number(r.max))) {
        return { min: Number(r.min), max: Number(r.max) };
    }
    return null;
}

/**
 * Owner enable/disable resolution: games[catId][key] ?? universal[key] ?? defaultEnabled.
 */
export function isFilterEnabled(filterSettings, entry, categoryId) {
    const perGame = filterSettings?.games?.[categoryId]?.[entry.key];
    if (typeof perGame === 'boolean') return perGame;
    const universal = filterSettings?.universal?.[entry.key];
    if (typeof universal === 'boolean') return universal;
    return entry.defaultEnabled !== false;
}

/**
 * Does a registry entry apply to this game? An `allGames` entry (Price) applies
 * to every game, including ones the `gameIds` list has never heard of, because
 * the kiosk sells whatever the store stocks. Every applicability check goes
 * through here so the list cannot quietly decide it again. TKT-2026-105611
 */
export function appliesToGame(entry, categoryId) {
    if (!entry || !categoryId) return false;
    return entry.allGames === true || (Array.isArray(entry.gameIds) && entry.gameIds.includes(categoryId));
}

/**
 * Compute the ordered, VISIBLE registry entries for a category: applicable to
 * the game AND owner-enabled AND has values in the store's in-stock set.
 *
 * @param {Array} registry registry entries (live preferred, else fallback)
 * @param {number} categoryId
 * @param {object} filterSettings owner settings (may be null)
 * @param {object} filterOptions /filters response (facets + ranges)
 */
export function visibleFiltersFor(registry, categoryId, filterSettings, filterOptions) {
    if (!categoryId || !Array.isArray(registry)) return [];
    return registry
        .filter((e) => appliesToGame(e, categoryId))
        .filter((e) => isFilterEnabled(filterSettings, e, categoryId))
        .filter((e) => {
            if (e.control === 'range') {
                const b = rangeBounds(filterOptions, e.key);
                return Boolean(b) && b.min < b.max;
            }
            if (e.control === 'toggle') {
                // No facet needed for a boolean; if the backend reported a facet
                // list for it, require it to be non-empty (e.g. no foils in stock).
                const f = filterOptions?.facets?.[e.key];
                return f === undefined || (Array.isArray(f) && f.length > 0);
            }
            // chips / typeahead
            return facetOptions(filterOptions, e.key).length > 0;
        })
        .sort((a, b) => {
            const ga = FILTER_GROUP_ORDER.indexOf(a.group);
            const gb = FILTER_GROUP_ORDER.indexOf(b.group);
            if (ga !== gb) return (ga === -1 ? 99 : ga) - (gb === -1 ? 99 : gb);
            return 0;
        });
}

/**
 * Translate the dynamic filter value map into search query params, using the
 * EXACT backend contract: `key[]` arrays for chips/typeahead, `<key>Min`/`<key>Max`
 * for ranges (only when narrower than full bounds), boolean for toggles.
 *
 * @param {object} values dynamic value map keyed by registry key
 * @param {Array} entries the registry entries currently in play
 * @param {object} filterOptions /filters response (for range bounds)
 */
export function buildDynamicParams(values, entries, filterOptions) {
    const params = {};
    if (!values || !Array.isArray(entries)) return params;
    const byKey = new Map(entries.map((e) => [e.key, e]));
    for (const [key, val] of Object.entries(values)) {
        const entry = byKey.get(key);
        if (!entry) continue;
        if (entry.control === 'range') {
            if (!val || typeof val !== 'object') continue;
            const bounds = rangeBounds(filterOptions, key);
            // Either side may be open ("$20 and up"). null / '' must stay open,
            // and Number(null) is 0, so check for them before converting.
            const side = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
            const lo = side(val.min);
            const hi = side(val.max);
            if (isCurrencyRange(entry)) {
                // A typed amount is sent as typed. The span in `ranges` moves
                // as other filters change, and a price the shopper chose must
                // not silently drop out of the query when it does.
                if (Number.isFinite(lo)) params[`${key}Min`] = lo;
                if (Number.isFinite(hi)) params[`${key}Max`] = hi;
                continue;
            }
            // Only emit a bound if the user moved that thumb off the extreme.
            if (Number.isFinite(lo) && (!bounds || lo > bounds.min)) params[`${key}Min`] = lo;
            if (Number.isFinite(hi) && (!bounds || hi < bounds.max)) params[`${key}Max`] = hi;
        } else if (entry.control === 'toggle') {
            if (val === true) params[key] = true;
        } else {
            // chips / typeahead → array
            if (Array.isArray(val) && val.length) params[key] = val;
        }
    }
    return params;
}

// ── Price (TKT-2026-105611) ─────────────────────────────────────────────────

/**
 * A money range. Keyed on the registry `format`, and on the price key itself
 * so the contract holds even against a registry served before `format` existed.
 */
export function isCurrencyRange(entry) {
    return Boolean(entry) && entry.control === 'range' && (entry.format === 'currency' || entry.key === PRICE_FILTER_KEY);
}

/**
 * Read one typed price box. Tolerates a pasted currency symbol or thousands
 * separator. Returns null for blank or unusable input, so a box the shopper
 * cleared means "no bound on this side" rather than 0.
 */
export function parsePriceInput(raw) {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).replace(/[^0-9.]/g, '');
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100) / 100;
}

/**
 * The value stored for the Price filter from the two boxes: {min,max} with
 * either side possibly null, swapped if entered backwards, or null when both
 * are blank (which removes the filter).
 */
export function priceRangeValue(minRaw, maxRaw) {
    let min = parsePriceInput(minRaw);
    let max = parsePriceInput(maxRaw);
    if (min === null && max === null) return null;
    if (min !== null && max !== null && min > max) [min, max] = [max, min];
    return { min, max };
}

/**
 * Whether the kiosk shows the Price control. Unlike every other registry
 * filter it does not need a game selected: a price means the same thing in
 * every game. It needs a real span to offer, the owner not to have switched it
 * off, and sell mode, since in buy mode the shopper is being quoted buylist
 * offers and the store's own prices do not apply.
 */
export function priceFilterVisible({ registry, filterSettings, categoryId, filterOptions, buylistMode }) {
    if (buylistMode) return false;
    const entry = (Array.isArray(registry) ? registry : []).find((e) => e.key === PRICE_FILTER_KEY)
        || FILTER_REGISTRY_FALLBACK.find((e) => e.key === PRICE_FILTER_KEY);
    if (categoryId && !appliesToGame(entry, categoryId)) return false;
    if (!isFilterEnabled(filterSettings, entry, categoryId)) return false;
    const b = rangeBounds(filterOptions, PRICE_FILTER_KEY);
    return Boolean(b) && b.min < b.max;
}

/**
 * Chip label text for a range value, e.g. "$5.00 – $15.00", "¥2,000+", "up to $3.00".
 * formatPrice is the store's money formatter (KioskThemeContext formatMoney), so
 * a price bound shows the store's symbol and decimals. TKT-2026-106987
 */
export function formatRangeValue(entry, val, formatPrice = (v) => formatMoney(v, null)) {
    const has = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
    const fmt = (v) => (isCurrencyRange(entry) ? formatPrice(Number(v)) : String(v));
    const lo = has(val?.min);
    const hi = has(val?.max);
    if (lo && hi) return `${fmt(val.min)} – ${fmt(val.max)}`;
    if (lo) return `${fmt(val.min)}+`;
    if (hi) return `up to ${fmt(val.max)}`;
    return '';
}
